import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  capitalize,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
  Alert } from '@mui/material';
import { ContainedButton } from 'components/ContainedButton';
import SelectField from 'components/select-field';
import { TFunction } from 'i18next';
import React, { FC } from 'react';
import { DropzoneInputProps, DropzoneRootProps } from 'react-dropzone';
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { DocumentsType } from 'v2-types/order';

import { useStyles } from './styles';
import { DocumentPartyUpload } from './types';

type ManualDocumentUploadModalSceneProps = {
  open: boolean;
  t: TFunction;
  handleClose: () => void;
  control: Control<DocumentPartyUpload, object>;
  watch: UseFormWatch<DocumentPartyUpload>;
  isValid: boolean;
  onPartySelect: ({ partyId }) => void;
  onUploadDocuments: () => void;
  onRemoveFile: ({ file }: { file: File }) => void;
  openFileDialog: () => void;
  getInputProps: <T extends DropzoneInputProps>(props?: T | undefined) => T;
  getRootProps: <T extends DropzoneRootProps>(props?: T | undefined) => T;
  filesToUpload: File[];
  isLoading: boolean;
  document: DocumentsType,
  selectedParty: string;
  setValue: UseFormSetValue<DocumentPartyUpload>;
};

export const ManualDocumentUploadModalScene: FC<ManualDocumentUploadModalSceneProps> = ({
  open,
  t,
  handleClose,
  control,
  watch,
  isValid,
  onPartySelect,
  onUploadDocuments,
  onRemoveFile,
  openFileDialog,
  getRootProps,
  getInputProps,
  filesToUpload,
  isLoading,
  document,
  selectedParty,
  setValue,
}) => {
  const classes = useStyles();
  const documentParties = document.data.filter(({ kind }) => kind === "party");
  const duplicatedFileNames = document.archives.find(({ _id }) => _id === selectedParty)?.files!
    .filter((file) => filesToUpload.find((uploaded) => uploaded.name.includes(file.filename))) ?? [];

  return (
    <Dialog open={open} fullWidth keepMounted={false}>
      <DialogContent style={{ padding: 0 }}>
        <Grid container direction="column">
          <Grid item>
            <Typography variant="h5" className={classes.title}>
              {t('documents:uploadPartyFiles')}
            </Typography>
          </Grid>
          <Grid
            item
            container
            direction="column"
            spacing={2}
            className={classes.container}
          >
            <Grid item>
              <Typography variant="body1" className={classes.subtitle}>
                {t('orders:selectPartyLabel')}
              </Typography>
            </Grid>
            {!!duplicatedFileNames.length && (
              <Grid item>
                <Alert
                  severity="error"
                  classes={{ icon: classes.errorIconContainer }}
                >
                  {`${t('documents:you-already-have')}${duplicatedFileNames
                    .map((file) => file.filename)
                    .join(', ')}`}
                </Alert>
              </Grid>
            )}
            <Grid item>
              <Controller
                control={control}
                name="partyId"
                rules={{ required: true }}
                render={({ field }) => (
                  <SelectField
                    {...field}
                    label={t('orders:selectPartyLabel')}
                    value={field.value || ''}
                    InputProps={{
                      ...field,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        onPartySelect({ partyId: e.target.value });
                        field.onChange(e);
                      },
                    }}
                    data={documentParties}
                    dataKey="_id"
                    dataValue="_id"
                    dataText="name"
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid
            item
            container
            justifyContent="center"
            className={classes.container}
            xs
          >
            <Grid
              item
              {...getRootProps({ className: 'dropzone' })}
              className={classes.dropzoneContainer}
            >
              <Grid
                container
                direction="column"
                alignItems="center"
                spacing={2}
              >
                <input {...getInputProps()} />
                <Grid item>
                  <FontAwesomeIcon icon="upload" size="2x" />
                </Grid>
                <Grid item>
                  <Typography variant="body2">
                    {capitalize(t('documents:drag-or-drop'))}
                  </Typography>
                </Grid>
                <Grid item>
                  <ContainedButton
                    onClick={openFileDialog}
                    text={capitalize(t('documents:browse-files'))}
                  />
                </Grid>
                <Grid container justifyContent="center" style={{ gap: '5px' }}>
                  {filesToUpload.map((file) => (
                    <Grid
                      item
                      container
                      className={classes.fileContainer}
                      alignItems="center"
                    >
                      <Grid item>
                        <Button
                          onClick={() => onRemoveFile({ file })}
                          style={{ minWidth: 'auto' }}
                        >
                          <Box className={classes.removeFileContainer}>
                            <FontAwesomeIcon
                              size="sm"
                              icon="plus"
                              color="white"
                              transform={{ rotate: 45 }}
                            />
                          </Box>
                        </Button>
                      </Grid>
                      <Grid item>
                        <Typography variant="caption">{file.name}</Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actionButtons}>
        <ContainedButton text={t('common:close')} onClick={handleClose} />
        <ContainedButton
          text={t('documents:upload')}
          isLoading={isLoading}
          onClick={onUploadDocuments}
          disabled={!isValid || filesToUpload.length === 0 || duplicatedFileNames.length > 0}
        />
      </DialogActions>
    </Dialog>
  );
};
